'use client'

import React, { createContext, ReactElement, useContext, useEffect, useRef, useState } from 'react'
import { Currency } from '@/types/Page'
import { Program, ProgramUpdate } from '@/types/Misc'
import { fetchHotelPrograms } from '@/lib/frontent-api'
import { ConfigContext } from '@/components/common/ConfigContext'
import { useDateFnsLocale } from '@/lib/utils/dateUtils'

interface HotelPageContextProps {
  modalOpen: boolean
  hideModal: () => void
  modelHasBeenOpened: () => void
  currency: Currency
  programs: Program[]
  additionalPrograms: Program[]
  getProgram: (programCode: string) => Program
}

interface HotelPageContextInputProps {
  children: ReactElement
  currency: Currency
  standardPrograms: Program[]
  hotelCode: string
}

export const HotelPageContext = createContext<HotelPageContextProps>({} as HotelPageContextProps)

function replaceHashWithQuestionMark(): void {
  const currentUrl = window.location.href
  const modifiedUrl = currentUrl.replace('#', '?')

  // Use history.pushState to change the URL without reloading the page
  window.history.replaceState({}, '', modifiedUrl)
}

export default function HotelPageContextProvider({
  children,
  currency,
  standardPrograms,
  hotelCode,
}: HotelPageContextInputProps): ReactElement {
  const [modalOpen, setModalOpen] = useState(false)
  const [programs, setPrograms] = useState<Program[]>(standardPrograms)
  const [additionalPrograms, setAdditionalPrograms] = useState<Program[]>([])
  const domain = useContext(ConfigContext)!.domain
  const locale = useContext(ConfigContext)!.locale
  const originalPrograms = useRef(
    Object.fromEntries(standardPrograms.map((program) => [program.programCode, program])),
  )
  useDateFnsLocale(locale) //Force loading of locale even if all hotel programs are closed

  function getProgram(programCode: string): Program {
    return (
      programs.find((program) => program.programCode === programCode) ||
      additionalPrograms.find((program) => program.programCode === programCode)!
    )
  }

  function getUrlParams(): URLSearchParams {
    return new URLSearchParams(window.location.search)
  }

  useEffect(() => {
    if (programs.length === 0 && additionalPrograms.length === 0) {
      console.error(
        'No programs available in original hotel page payload. - ' +
          window.location.href +
          ' - ' +
          JSON.stringify(programs),
      )
    }
  }, [])

  useEffect(() => {
    replaceHashWithQuestionMark()
    const params = getUrlParams()
    params.delete('p')
    params.delete('dateExact')
    params.delete('source')
    if (params.size > 0) {
      void fetchHotelPrograms(hotelCode, domain, getUrlParams(), currency).then(
        (programsUpdate) => {
          function updateProgram(programUpdate: ProgramUpdate): Program {
            return {
              ...originalPrograms.current[programUpdate.programCode],
              ...programUpdate,
            }
          }

          if (
            programsUpdate.programs.length === 0 &&
            programsUpdate.additionalPrograms.length === 0
          ) {
            console.error(
              'No programs available in program update. - ' +
                window.location.href +
                ' - ' +
                JSON.stringify(programsUpdate),
            )
          }

          setPrograms(programsUpdate.programs.map(updateProgram))
          setAdditionalPrograms(programsUpdate.additionalPrograms.map(updateProgram))
        },
      )
    }
  }, [hotelCode, domain])

  function modelHasBeenOpened(): void {
    setModalOpen(true)
  }

  function hideModal(): void {
    setModalOpen(false)
  }

  const context = {
    modalOpen,
    hideModal,
    modelHasBeenOpened,
    currency,
    programs,
    additionalPrograms,
    getProgram,
  }

  return <HotelPageContext.Provider value={context}>{children}</HotelPageContext.Provider>
}
